/**
 * Runs & Audit Evidence — immutable decision timeline with the reproducibility
 * tuple and response/outcome status.
 *
 * Reads GET /api/agentic/runs (backend/app/routes/agentic_governance.py) over
 * the append-only agent_decisions ledger. Rows link to the Decision card for
 * the full evidence trail. On 403/404 → NotEnabled.
 */
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronRight, ScrollText } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Icon,
  PageHeader,
  Section,
  Spinner,
  Stack,
  Text,
} from '@smms/design-system';
import { getRuns, type RunItem } from '../api/agentic';
import { isNotEntitled } from '../api/client';
import NotEnabled from '../components/NotEnabled';

function humanize(s: string): string {
  return s.replaceAll('_', ' ');
}

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/** Short reproducibility signature — model + prompt + policy + config. */
function versionSig(v: Record<string, string | null>): string {
  return [v.model_version, v.prompt_version, v.policy_version, v.config_version]
    .filter(Boolean)
    .join(' · ');
}

export default function RunsPage() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['agentic', 'runs'],
    queryFn: getRuns,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="runs-loading">
        <Spinner size="lg" label="Loading audit timeline" />
      </div>
    );
  }

  if (error) {
    if (isNotEntitled(error)) return <NotEnabled />;
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-8">
        <EmptyState
          variant="error"
          icon={<Icon as={AlertTriangle} size="lg" />}
          title="Couldn't load the audit timeline"
          description="Something went wrong while loading runs. Try again."
          action={<Button onClick={() => void refetch()}>Retry</Button>}
        />
      </div>
    );
  }

  if (!data) return <NotEnabled />;

  return (
    <div className="mx-auto w-full max-w-5xl p-6">
      <Stack gap="lg">
        <PageHeader
          title="Runs & Audit Evidence"
          description="Immutable, append-only timeline of every agent decision — with the reproducibility tuple (model/prompt/policy/config) and response/outcome status. Read-only; open a row for full evidence."
        />

        <Section>
          {data.items.length === 0 ? (
            <Card padding="lg">
              <EmptyState
                compact
                icon={<Icon as={ScrollText} size="lg" />}
                title="No decisions recorded yet"
                description="Every agent decision lands in the immutable ledger and appears here with its full audit trail."
              />
            </Card>
          ) : (
            <Card padding="none">
              <ul className="divide-y divide-border" data-testid="runs-list">
                {data.items.map((r: RunItem) => (
                  <li key={r.id}>
                    <Link
                      to={`/decisions/${r.id}`}
                      className="flex flex-wrap items-center gap-3 px-4 py-3 transition-colors hover:bg-elevated"
                    >
                      <div className="min-w-0 flex-1">
                        <Text variant="body-sm" className="font-medium">
                          {humanize(r.decision_type)}
                        </Text>
                        <Text variant="caption" tone="muted">
                          {humanize(r.agent_id)} · {r.autonomy_phase} · {formatWhen(r.created_at)}
                        </Text>
                        <Text variant="mono" tone="subtle" className="mt-0.5 block text-xs">
                          {versionSig(r.versions)}
                        </Text>
                      </div>
                      {r.latest_response_type && (
                        <Badge variant="info" size="sm">
                          {humanize(r.latest_response_type)}
                        </Badge>
                      )}
                      {r.has_outcome && (
                        <Badge variant="success" size="sm">
                          executed
                        </Badge>
                      )}
                      <Icon as={ChevronRight} size="sm" className="text-muted" />
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </Section>
      </Stack>
    </div>
  );
}
